import { useParams, Navigate, Link } from "react-router-dom";
import { ChevronRight, ArrowRight, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { AdUnit } from "@/components/AdUnit";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BLOG_POSTS } from "@/data/blog";

function renderBlock(b, i) {
  if (b.p) return <p key={i} className="text-base text-slate-600 leading-relaxed mt-4">{b.p}</p>;
  if (b.ul) return (
    <ul key={i} className="mt-4 space-y-2">
      {b.ul.map((it, j) => (
        <li key={j} className="flex items-start gap-3 text-base text-slate-600">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 mt-2.5" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  if (b.ol) return (
    <ol key={i} className="mt-4 space-y-3">
      {b.ol.map((it, j) => (
        <li key={j} className="flex items-start gap-3">
          <span className="w-7 h-7 rounded-lg brand-gradient text-white font-bold flex items-center justify-center shrink-0 text-xs mt-0.5">{j + 1}</span>
          <span className="text-base text-slate-600 pt-0.5">{it}</span>
        </li>
      ))}
    </ol>
  );
  if (b.h3) return (
    <div key={i} className="mt-5">
      <h3 className="text-lg font-bold text-slate-900">{b.h3}</h3>
      {(b.blocks || []).map((sub, j) => renderBlock(sub, `${i}-${j}`))}
    </div>
  );
  return null;
}

function Blocks({ blocks }) {
  return <>{blocks.map((b, i) => renderBlock(b, i))}</>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];
  if (!post) return <Navigate to="/blog" replace />;

  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.ogTitle,
    url: `https://ecroplyai.in${post.path}`,
    image: `https://ecroplyai.in${post.hero}`,
    author: { "@type": "Organization", name: "Ecroply AI" },
    publisher: { "@type": "Organization", name: "Ecroply AI" },
  }];

  return (
    <>
      <Seo
        title={post.title}
        description={post.metaDesc}
        path={post.path}
        faq={post.faq}
        jsonLd={jsonLd}
        ogImage={post.hero}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.h1, path: post.path }]}
      />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-200 truncate">{post.h1}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">{post.h1}</h1>
          <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {post.hero && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-2">
          <img src={post.hero} alt={post.heroAlt} data-testid="article-hero"
            className="w-full rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60" loading="eager" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {post.sections.map((s, i) => (
          <section key={i} className="mb-8" data-testid={`blog-section-${i}`}>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{s.h2}</h2>
            <Blocks blocks={s.blocks} />
          </section>
        ))}

        <AdUnit />

        {post.faq && post.faq.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {post.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-slate-900 font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {post.links && post.links.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">Tools mentioned in this guide</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.links.map((r) => (
                <Link key={r.path} to={r.path} data-testid={`blog-related-${r.path}`} className="group flex items-center justify-between bg-white border border-slate-200 hover:border-blue-400 rounded-xl p-5 transition-colors">
                  <span className="font-semibold text-slate-800 group-hover:text-blue-700">{r.label}</span>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-4 h-4" /> Back to all guides
          </Link>
        </div>
      </article>
    </>
  );
}
